import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import React, { useCallback, useEffect, useState, useRef } from "react";
import useAirportFetch from "../hooks/useAirportFetch";
import { useTranslation } from "react-i18next";

const UZ_AIRPORTS = [
  { code: "UZTT", name: "Toshkent / Tashkent" },
  { code: "UZSS", name: "Samarqand / Samarkand" },
  { code: "UZSB", name: "Buxoro / Bukhara" },
  { code: "UZNU", name: "Urganch / Urgench" },
  { code: "UZNN", name: "Nukus" },
  { code: "UZFF", name: "Farg'ona / Fergana" },
  { code: "UZFA", name: "Andijon / Andijan" },
  { code: "UZFN", name: "Namangan" },
  { code: "UZST", name: "Termiz / Termez" },
  { code: "UZSK", name: "Qarshi / Karshi" },
  { code: "UZSA", name: "Navoiy / Navoi" },
];

const GLOBAL_AIRPORTS = [
  { code: "OMDB", name: "Dubai International" },
  { code: "LTFM", name: "Istanbul" },
  { code: "UUEE", name: "Moscow Sheremetyevo" },
  { code: "EGLL", name: "London Heathrow" },
  { code: "EDDF", name: "Frankfurt" },
  { code: "LFPG", name: "Paris Charles de Gaulle" },
  { code: "ZBAA", name: "Beijing Capital" },
  { code: "RJTT", name: "Tokyo Haneda" },
];

const INPUT_ID = "icao-input";
const ERROR_ID = "icao-error";
const HINT_ID = "icao-hint";

export default function AirportSelectInput({ initialValue, initialError, onDataLoaded }) {
  const { t } = useTranslation();
  const [icaoValue, setIcaoValue] = useState(initialValue ?? "");
  const [error, setError] = useState(initialError ?? null);
  const [edited, setEdited] = useState(false);
  const [isLoading, setLoading] = useState(false);
  
  // Custom dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const debouncedIcaoValue = useDebounced(icaoValue, 500);

  const handleIcaoValueChange = useCallback((e) => {
    setError(null);
    setIcaoValue(e.target.value.toUpperCase());
    setEdited(true);
  }, []);

  const handleOptionSelect = useCallback((code) => {
    setError(null);
    setIcaoValue(code.toUpperCase());
    setEdited(true);
    setIsDropdownOpen(false);
  }, []);

  const fetchData = useAirportFetch({
    onLoading: setLoading,
    onError: setError,
    onLoaded: onDataLoaded,
  });

  useEffect(() => {
    if (debouncedIcaoValue && debouncedIcaoValue.length >= 3) {
      fetchData(debouncedIcaoValue);
    } else if (edited) {
      setLoading(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedIcaoValue]);

  // Click outside to close custom dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const describedBy = [error ? ERROR_ID : null, HINT_ID].filter(Boolean).join(" ");

  return (
    <div className="space-y-4">
      {/* Error message */}
      {error && (
        <div
          id={ERROR_ID}
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium"
        >
          <FontAwesomeIcon
            icon={["fas", "triangle-exclamation"]}
            className="mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <span>{error}</span>
        </div>
      )}

      {/* ICAO text input */}
      <div>
        <label
          htmlFor={INPUT_ID}
          className="block text-xs font-bold text-slate-400 tracking-widest uppercase mb-2"
        >
          {t("enterIcao")}
        </label>
        <div className="relative">
          <input
            id={INPUT_ID}
            value={icaoValue}
            onChange={handleIcaoValueChange}
            type="text"
            maxLength={6}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            aria-invalid={!!error}
            aria-describedby={describedBy || undefined}
            aria-busy={isLoading}
            placeholder="UZTT"
            className={clsx(
              "block w-full h-14 px-4 pr-12",
              "bg-white/5 border rounded-lg",
              "text-2xl font-bold tracking-widest text-center text-white uppercase",
              "placeholder-slate-700 focus:placeholder-transparent",
              "transition-all duration-200 outline-none",
              error
                ? "border-red-500/50 focus:border-red-400"
                : "border-white/10 focus:border-aviation-cyan/60"
            )}
          />

          {/* Loading / error / ready icon */}
          <div
            className={clsx(
              "absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none",
              error ? "text-red-400" : "text-aviation-cyan"
            )}
            aria-hidden="true"
          >
            {isLoading && (
              <FontAwesomeIcon
                icon={["fas", "circle-notch"]}
                spin
                className="w-4 h-4"
              />
            )}
            {!isLoading && error && (
              <FontAwesomeIcon
                icon={["fas", "triangle-exclamation"]}
                className="w-4 h-4"
              />
            )}
            {!isLoading && !error && icaoValue.length >= 3 && (
              <FontAwesomeIcon
                icon={["fas", "magnifying-glass"]}
                className="w-3 h-3 opacity-40"
              />
            )}
          </div>
        </div>
        <p id={HINT_ID} className="mt-1 text-xs text-slate-600">
          {t("select.subtitle")}
        </p>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3" aria-hidden="true">
        <div className="flex-1 h-px bg-white/[0.06]" />
        <span className="text-xs text-slate-600 uppercase tracking-wider">
          {t("common.or")}
        </span>
        <div className="flex-1 h-px bg-white/[0.06]" />
      </div>

      {/* Custom Airport list dropdown */}
      <div className="relative" ref={dropdownRef}>
        <label
          className="block text-xs font-bold text-slate-400 tracking-widest uppercase mb-2"
        >
          {t("airportList")}
        </label>
        
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={clsx(
            "w-full h-11 px-4 flex items-center justify-between",
            "bg-white/5 border border-white/10 rounded-lg",
            "text-sm text-slate-300",
            "hover:border-aviation-cyan/40 hover:bg-white/10",
            "focus:border-aviation-cyan/60 focus:outline-none focus:ring-1 focus:ring-aviation-cyan/50",
            "transition-all duration-200"
          )}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
        >
          <span>— {t("airportList")} —</span>
          <FontAwesomeIcon
            icon={["fas", "chevron-down"]}
            className={clsx("transition-transform duration-300", isDropdownOpen ? "rotate-180 text-aviation-cyan" : "text-slate-500")}
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute z-[100] left-0 top-full w-full mt-2 rounded-lg border border-white/10 bg-[#0a0e1a] bg-opacity-95 backdrop-blur-xl shadow-2xl overflow-hidden animate-fade-in origin-top">
            <ul className="max-h-64 overflow-y-auto custom-scrollbar py-2" role="listbox">
              
              {/* Group: UZ */}
              <li className="px-4 py-2 text-xs font-bold text-aviation-cyan tracking-widest uppercase bg-slate-800/80 sticky top-0 backdrop-blur-md z-10 border-b border-slate-700/50">
                🇺🇿 {t("select.uzAirports")}
              </li>
              {UZ_AIRPORTS.map(({ code, name }) => (
                <li
                  key={code}
                  role="option"
                  aria-selected={icaoValue === code}
                  onClick={() => handleOptionSelect(code)}
                  className="px-4 py-3 text-sm text-slate-300 hover:bg-aviation-cyan/20 hover:text-white cursor-pointer transition-colors border-b border-white/5 last:border-0 flex items-center gap-3 group"
                >
                  <span className="font-mono font-bold text-slate-400 group-hover:text-aviation-cyan transition-colors">{code}</span>
                  <span className="truncate">{name}</span>
                </li>
              ))}
              
              {/* Group: Global */}
              <li className="px-4 py-2 mt-2 text-xs font-bold text-aviation-cyan tracking-widest uppercase bg-slate-800/80 sticky top-0 backdrop-blur-md z-10 border-y border-slate-700/50">
                🌍 {t("select.international")}
              </li>
              {GLOBAL_AIRPORTS.map(({ code, name }) => (
                <li
                  key={code}
                  role="option"
                  aria-selected={icaoValue === code}
                  onClick={() => handleOptionSelect(code)}
                  className="px-4 py-3 text-sm text-slate-300 hover:bg-aviation-cyan/20 hover:text-white cursor-pointer transition-colors border-b border-white/5 last:border-0 flex items-center gap-3 group"
                >
                  <span className="font-mono font-bold text-slate-400 group-hover:text-aviation-cyan transition-colors">{code}</span>
                  <span className="truncate">{name}</span>
                </li>
              ))}
              
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function useDebounced(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
